import { useState } from 'react';
import { api, useApi } from '../api';
import { Card, ErrorBanner, Loading } from '../components/ui';
import { useT, type Clave } from '../i18n';

/**
 * Con qué se genera cada cosa.
 *
 * ── Por qué es una pantalla y no una tarjeta dentro de Consumo ────────────────
 * Porque son dos preguntas distintas y estaban juntas. Consumo responde «cuánto se gastó», que se
 * mira; esto responde «con qué se genera», que se decide. Mezcladas, para cambiar un modelo había
 * que pasar por encima de tres tablas de consumo, y para mirar el gasto había que esquivar cinco
 * desplegables.
 *
 * ── Por qué el orden es SFW y después NSFW ────────────────────────────────────
 * Porque es la división que decide todo lo demás: qué modelo puede usarse, si hay filtro de por
 * medio, y adónde se puede publicar lo que salga. Dentro de cada uno, el orden es el de la
 * cadena: texto, imagen, video.
 */

type Modelo = { id: string; etiqueta: string; nota?: string };

/**
 * Cómo se llama cada perfil para quien lo lee.
 *
 * Las claves son internas —`unrestricted_seedream5` no le dice nada a nadie— y lo que decide al
 * elegir es qué cuesta y qué acepta, no cómo se llama la constante.
 */
const NOMBRE_DEL_PERFIL: Record<string, Clave> = {
  balanced: 'mod.perfilGratis',
  unrestricted_seedream: 'mod.perfilPago',
};

type Catalogo = {
  /*
    Casi todos los ajustes de esta pantalla son «un modelo elegido», o sea una cadena. La pila de
    LoRA de la placa no: es una lista de archivos con su peso. Se ensancha el tipo para esa clave
    en vez de aflojar el `Record` entero, que dejaría pasar cualquier cosa en las demás.
  */
  settings: Record<string, string | null> & {
    localGpuLoras?: Record<string, { archivo: string; peso: number }[]> | null;
    lorasDeCuerpo?: Record<
      string,
      { archivo: string; pesos: Record<string, number> } | null
    > | null;
  };
  /** Qué modelo pone cada perfil en cada etapa, con su nombre legible. Lo calcula el servidor. */
  planPorPerfil?: Record<string, Record<string, string>>;
  catalogoDeModelos?: {
    model: string;
    provider: string;
    etiqueta: string;
    etapas: (1 | 2 | 3)[];
    disponible: boolean;
  }[];
  catalogoDeTexto?: { id: string; etiqueta: string; air: string; nota?: string }[];
  catalogoDeCaption?: { id: string; etiqueta: string; air: string; nota?: string }[];
  catalogoNsfw?: { imagen: Modelo[]; video: Modelo[]; nota: string; faltan: string };
  /** Los que editan una imagen ya hecha. Es otra lista porque es otro paso, no una alternativa. */
  editoresDeImagen?: {
    id: string;
    etiqueta: string;
    air: string;
    usdPorSalida: number;
    usdPorEntrada: number;
    sinFiltro: boolean;
    nota: string;
  }[];
};

/** Un desplegable que guarda solo. Todos los de esta pantalla hacen lo mismo. */
function Elegir({
  clave,
  titulo,
  detalle,
  valor,
  opciones,
  vacio,
}: {
  clave: string;
  titulo: string;
  detalle: string;
  valor: string | null;
  opciones: { value: string; label: string; disabled?: boolean }[];
  /**
   * Qué dice la opción vacía, cuando la hay.
   *
   * Sin ella el desplegable no tiene estado «no elegido»: es el caso del perfil de calidad, que
   * siempre vale algo. Ofrecerle una opción vacía mandaría `null` a un campo que no lo admite y
   * el guardado fallaría con un 400 sin que nadie entienda por qué.
   */
  vacio?: string;
}) {
  return (
    <div>
      <label htmlFor={clave} className="block text-sm font-medium">
        {titulo}
      </label>
      <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">{detalle}</p>
      <select
        id={clave}
        defaultValue={valor ?? ''}
        onChange={(event) =>
          void api.patch('/api/settings', { [clave]: event.target.value || null })
        }
        className="mt-1.5 w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
      >
        {vacio !== undefined && <option value="">{vacio}</option>}
        {opciones.map((opcion) => (
          <option key={opcion.value} value={opcion.value} disabled={opcion.disabled}>
            {opcion.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Qué workflow de la placa corresponde a cada etapa del sistema.
 *
 * El identificador del modelo es lo que el proveedor sabe de sí mismo, y el nombre del archivo es
 * lo que el puente carga. La tabla los une, y existe acá porque es una decisión de producto —qué
 * etapa usa qué grafo— y no un detalle de ninguno de los dos lados.
 */
/** El nombre a secas, para que el resumen de una etapa entre en una línea. */
const nombreCorto = (archivo: string): string =>
  archivo
    .split('/')
    .pop()
    ?.replace(/\.safetensors$/, '') ?? archivo;

/** Los pesos que se ofrecen. El valor que ya tenga un LoRA se agrega aparte. */
const PESOS_SUGERIDOS = [0.2, 0.4, 0.5, 0.6, 0.8, 0.9, 1, 1.2];

const WORKFLOW_DE_ETAPA: { modelo: string; archivo: string; etiqueta: string }[] = [
  { modelo: 'local-gpu/face', archivo: 'face.json', etiqueta: 'Candidatas frontales' },
  { modelo: 'local-gpu/identity', archivo: 'identity.json', etiqueta: 'Hoja de identidad' },
  { modelo: 'local-gpu/content-sfw', archivo: 'content_sfw.json', etiqueta: 'Contenido' },
  {
    modelo: 'local-gpu/content-nsfw',
    archivo: 'content_nsfw.json',
    etiqueta: 'Contenido explícito',
  },
];

/**
 * La pila de LoRA de estilo de la placa, una por etapa.
 *
 * ── Por qué una por etapa y no una sola ───────────────────────────────────────
 * Porque no llevan lo mismo, y la diferencia no es de matiz. Medido sobre los workflows reales:
 * `face` usa dos —piel y teléfono— y ni siquiera lleva el de identidad, porque no hay referencia
 * que preservar; `identity` usa tres; `content-sfw` cinco, que es lo que le da el aspecto de foto;
 * y `content-nsfw` seis, porque suma `snofs`, que es lo que destraba el desnudo. Una pila única
 * obligaría a las cuatro a compartir la de la más cargada.
 *
 * ── Por qué arranca marcado y no vacío ────────────────────────────────────────
 * Porque sin configurar el sistema **sí usa** LoRA: los que trae el workflow. La primera versión
 * de esta pantalla mostraba la lista vacía y parecía que no se usaba ninguno cuando se usaban
 * cinco. Un ajuste sin tocar tiene que verse como lo que hace hoy, no como cero — por eso el
 * estado inicial se lee del propio grafo, vía `/api/gpu/workflows`.
 *
 * ── Qué no aparece, y por qué ─────────────────────────────────────────────────
 * · El LoRA de identidad: no describe un aspecto, es el mecanismo por el que la referencia
 *   significa algo. Apagarlo no daría una foto más fea sino otra persona; el puente lo conserva
 *   siempre y ofrecerlo acá invitaría a apagarlo por error.
 * · Los de personaje: son de una sola influencer y se eligen en su ficha.
 * · Los que no son de esta arquitectura. La carpeta tiene LoRA de Wan, de LTX y hasta uno del
 *   codificador de texto de Gemma; ninguno hace nada sobre Krea 2, y ofrecerlos es invitar a un
 *   cambio que no cambia nada.
 * · Los workflows de video: cargan sus LoRA cableados nodo a nodo al modelo alto y al bajo de
 *   Wan 2.2, no como lista. No es una pila y no se puede editar como si lo fuera.
 */
function PilaDeUnaEtapa({
  etapa,
  delWorkflow,
  candidatos,
  configurada,
  guardando,
  onGuardar,
}: {
  etapa: { modelo: string; archivo: string; etiqueta: string };
  delWorkflow: { archivo: string; peso: number; on: boolean }[] | null;
  candidatos: { archivo: string; nombre: string; bytes: number }[];
  configurada: { archivo: string; peso: number }[] | undefined;
  guardando: boolean;
  onGuardar: (pila: { archivo: string; peso: number }[] | undefined) => void;
}) {
  const { t } = useT();
  const [abierta, setAbierta] = useState(false);

  if (!delWorkflow) return null;

  /* Sin ajuste guardado, el estado visible es el del grafo: los que trae encendidos. */
  const efectiva =
    configurada ??
    delWorkflow
      .filter((l) => l.on && !l.archivo.includes('identity_edit'))
      .map((l) => ({
        archivo: l.archivo,
        peso: l.peso,
      }));

  const puesto = (archivo: string) => efectiva.find((l) => l.archivo === archivo);

  /*
    Colapsada, y el resumen dice lo que aplica hoy.

    Son veinticinco LoRA por etapa y cuatro etapas: desplegadas, la pantalla son cien casillas de
    las que importan cinco. El resumen —nombre y peso de los que están puestos— es lo que se viene
    a mirar el noventa por ciento de las veces, y cabe en una línea.
  */
  const resumen =
    efectiva.length === 0
      ? t('pila.ninguno')
      : efectiva.map((l) => `${nombreCorto(l.archivo)} ${l.peso}`).join(' · ');

  return (
    <div className="mt-4 border-t border-[var(--color-border-subtle)] pt-3">
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        className="flex w-full flex-wrap items-baseline gap-2 text-left"
      >
        <span className="text-xs text-[var(--color-ink-muted)]">{abierta ? '▾' : '▸'}</span>
        <strong className="text-sm">{etapa.etiqueta}</strong>
        <code className="font-mono text-xs text-[var(--color-ink-muted)]">{etapa.archivo}</code>
        <span className="ml-auto text-xs text-[var(--color-ink-muted)]">
          {configurada ? t('pila.configurada') : t('pila.delWorkflow')}
        </span>
      </button>

      <p className="mt-1 pl-5 font-mono text-xs break-words text-[var(--color-ink-muted)]">
        {resumen}
      </p>

      {!abierta ? null : (
        <ul className="mt-2 space-y-1">
          {candidatos.map((lora) => {
            const activo = puesto(lora.archivo);
            return (
              <li
                key={lora.archivo}
                className={
                  'flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-sm ' +
                  (activo
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]'
                    : 'border-[var(--color-border-subtle)]')
                }
              >
                <input
                  type="checkbox"
                  checked={Boolean(activo)}
                  disabled={guardando}
                  onChange={(event) =>
                    onGuardar(
                      event.target.checked
                        ? [...efectiva, { archivo: lora.archivo, peso: 0.8 }]
                        : efectiva.filter((l) => l.archivo !== lora.archivo),
                    )
                  }
                />
                <span className="font-mono text-xs break-all">{lora.nombre}</span>
                <span className="ml-auto flex items-center gap-2">
                  {activo && (
                    <select
                      value={activo.peso}
                      disabled={guardando}
                      onChange={(event) =>
                        onGuardar(
                          efectiva.map((l) =>
                            l.archivo === lora.archivo
                              ? { ...l, peso: Number(event.target.value) }
                              : l,
                          ),
                        )
                      }
                      className="rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-2 py-1 text-xs"
                    >
                      {/*
                      El valor actual va SIEMPRE en la lista, aunque no sea uno de los sugeridos.

                      Sin esto, un peso que no estuviera entre las opciones —0,9, que es el que
                      traen puesto `skindetails` y `phone_photography`— hacía que el `select`
                      cayera a la primera opción: la pantalla mostraba 0,2 sobre un LoRA que vale
                      0,9, y al tocar cualquier otra cosa guardaba ese 0,2. Un desplegable que no
                      puede representar su propio valor miente dos veces.
                    */}
                      {[...new Set([...PESOS_SUGERIDOS, activo.peso])]
                        .sort((a, b) => a - b)
                        .map((n) => (
                          <option key={n} value={n}>
                            {n.toFixed(2).replace(/0$/, '')}
                          </option>
                        ))}
                    </select>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {abierta && configurada && (
        <button
          type="button"
          disabled={guardando}
          onClick={() => onGuardar(undefined)}
          className="mt-2 rounded-lg border border-[var(--color-border-subtle)] px-3 py-1 text-xs transition hover:border-[var(--color-accent)] disabled:opacity-40"
        >
          {t('pila.volverAlWorkflow')}
        </button>
      )}
    </div>
  );
}

/**
 * El LoRA que dibuja la FORMA del cuerpo, por perfil y por nivel de piel.
 *
 * ── Por qué es una perilla y no una constante ─────────────────────────────────
 * De la rampa entre `calle` y `explicito` hay DOS puntos medidos —0,8 iguala la referencia
 * anterior, 1,0 empieza a estilizar— y los cuatro del medio son interpolación. Es exactamente la
 * clase de valor que se ajusta mirando resultados, y tenerlo sólo en el código significa un
 * despliegue por cada vuelta.
 *
 * ── Por qué está separado de la pila de estilo ────────────────────────────────
 * La pila describe cómo se ven las fotos de este sistema y es global. Esto describe el CUERPO de
 * una influencer según su perfil, y se aplica en un solo sitio: la referencia de cuerpo. Meterlos
 * en la misma tarjeta invitaría a apagar uno creyendo que se apaga el otro.
 */
const NIVELES = [
  'calle',
  'deportivo',
  'playa',
  'sensual',
  'atrevido',
  'insinuado',
  'explicito',
] as const;
const PERFILES = ['curvilineo', 'atletico', 'delgado'] as const;

function FormaDelCuerpo({
  settings,
  onGuardado,
}: {
  settings: {
    lorasDeCuerpo?: Record<
      string,
      { archivo: string; pesos: Record<string, number> } | null
    > | null;
  };
  onGuardado: () => void;
}) {
  const { t } = useT();
  const instalados = useApi<{
    loras: { archivo: string; nombre: string; familia: string | null }[];
  }>('/api/gpu/loras');

  const [config, setConfig] = useState(settings.lorasDeCuerpo ?? {});
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  const candidatos = (instalados.data?.loras ?? []).filter(
    (l) => l.familia === 'krea-2' && !l.archivo.includes('identity_edit'),
  );

  const guardar = (siguiente: typeof config) => {
    setConfig(siguiente);
    setGuardando(true);
    setFallo(null);
    void api
      .patch('/api/settings', {
        lorasDeCuerpo: Object.keys(siguiente).length > 0 ? siguiente : null,
      })
      .then(() => onGuardado())
      .catch((caught: unknown) =>
        setFallo(caught instanceof Error ? caught.message : String(caught)),
      )
      .finally(() => setGuardando(false));
  };

  return (
    <Card title={t('cuerpo.tarjeta')}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-center gap-2 text-left text-sm"
      >
        <span className="text-xs text-[var(--color-ink-muted)]">{abierto ? '▾' : '▸'}</span>
        <span className="text-[var(--color-ink-muted)]">
          {Object.keys(config).length > 0
            ? t('pila.resumenConfiguradas', { n: Object.keys(config).length })
            : t('pila.resumenSinTocar')}
        </span>
      </button>

      {!abierto ? null : (
        <>
          <p className="mt-3 text-sm text-[var(--color-ink-muted)]">{t('cuerpo.que')}</p>
          <p className="mt-2 text-xs text-[var(--color-ink-muted)]">{t('cuerpo.medido')}</p>

          {PERFILES.map((perfil) => {
            const actual = config[perfil];
            return (
              <div
                key={perfil}
                className="mt-4 rounded-xl border border-[var(--color-border-subtle)] p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium capitalize">{perfil}</span>
                  <select
                    disabled={guardando}
                    value={actual === null ? '' : (actual?.archivo ?? '__default__')}
                    onChange={(e) => {
                      const v = e.target.value;
                      const siguiente = { ...config };
                      if (v === '__default__') delete siguiente[perfil];
                      else if (v === '') siguiente[perfil] = null;
                      else
                        siguiente[perfil] = {
                          archivo: v,
                          pesos: actual?.pesos ?? {},
                        };
                      guardar(siguiente);
                    }}
                    className="rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-2 py-1 text-xs"
                  >
                    <option value="__default__">{t('cuerpo.porDefecto')}</option>
                    <option value="">{t('cuerpo.sinLora')}</option>
                    {candidatos.map((l) => (
                      <option key={l.archivo} value={l.archivo}>
                        {l.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {actual && (
                  <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
                    {NIVELES.map((nivel) => (
                      <label key={nivel} className="text-[11px] text-[var(--color-ink-muted)]">
                        {nivel}
                        <input
                          type="number"
                          step="0.05"
                          min="0"
                          max="2"
                          disabled={guardando}
                          value={actual.pesos[nivel] ?? ''}
                          placeholder="—"
                          onChange={(e) => {
                            const pesos = { ...actual.pesos };
                            if (e.target.value === '') delete pesos[nivel];
                            else pesos[nivel] = Number(e.target.value);
                            guardar({ ...config, [perfil]: { ...actual, pesos } });
                          }}
                          className="mt-1 w-full rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-2 py-1 text-xs"
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {fallo && (
        <p className="mt-3 rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/5 p-3 text-xs break-words">
          {fallo}
        </p>
      )}
    </Card>
  );
}

function PilaDeLaPlaca({
  settings,
  onGuardado,
}: {
  settings: { localGpuLoras?: Record<string, { archivo: string; peso: number }[]> | null };
  onGuardado: () => void;
}) {
  const { t } = useT();
  const instalados = useApi<{
    loras: {
      archivo: string;
      nombre: string;
      familia: string | null;
      personaje: string | null;
      bytes: number;
    }[];
  }>('/api/gpu/loras');
  const grafos = useApi<{
    workflows: {
      archivo: string;
      pila: { archivo: string; peso: number; on: boolean }[] | null;
      identidad: string;
    }[];
  }>('/api/gpu/workflows');

  const [pilas, setPilas] = useState(settings.localGpuLoras ?? {});
  /*
    Colapsada de entrada. Son cuatro etapas por veinticinco LoRA, y esta tarjeta se toca una vez
    cada tanto: desplegada por defecto empuja hacia abajo todo lo que sí se mira seguido.
  */
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  /*
    Sólo los de estilo de ESTA arquitectura: ni los de personaje —que son de una sola influencer—
    ni los de otra familia. La carpeta tiene LoRA de Wan, de LTX y uno del codificador de Gemma, y
    ninguno hace nada sobre Krea 2.
  */
  const candidatos = (instalados.data?.loras ?? []).filter(
    (l) => l.personaje === null && l.familia === 'krea-2' && !l.archivo.includes('identity_edit'),
  );

  const guardar = (modelo: string, pila: { archivo: string; peso: number }[] | undefined) => {
    const siguiente = { ...pilas };
    if (pila === undefined) delete siguiente[modelo];
    else siguiente[modelo] = pila;

    setPilas(siguiente);
    setGuardando(true);
    setFallo(null);
    void api
      .patch('/api/settings', {
        localGpuLoras: Object.keys(siguiente).length > 0 ? siguiente : null,
      })
      .then(() => onGuardado())
      .catch((caught: unknown) =>
        setFallo(caught instanceof Error ? caught.message : String(caught)),
      )
      .finally(() => setGuardando(false));
  };

  const cuantasConfiguradas = Object.keys(pilas).length;

  return (
    <Card title={t('pila.tarjeta')}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-center gap-2 text-left text-sm"
      >
        <span className="text-xs text-[var(--color-ink-muted)]">{abierto ? '▾' : '▸'}</span>
        <span className="text-[var(--color-ink-muted)]">
          {cuantasConfiguradas > 0
            ? t('pila.resumenConfiguradas', { n: cuantasConfiguradas })
            : t('pila.resumenSinTocar')}
        </span>
      </button>

      {!abierto ? null : (
        <>
          <p className="mt-3 text-sm text-[var(--color-ink-muted)]">{t('pila.que')}</p>

          {(instalados.error ?? grafos.error) && (
            <p className="mt-3 rounded-xl border border-[var(--color-warn)]/40 p-3 text-sm">
              {t('pila.noSePudoListar', {
                motivo: (instalados.error ?? grafos.error)?.message ?? '',
              })}
            </p>
          )}

          {WORKFLOW_DE_ETAPA.map((etapa) => (
            <PilaDeUnaEtapa
              key={etapa.modelo}
              etapa={etapa}
              delWorkflow={
                grafos.data?.workflows.find((w) => w.archivo === etapa.archivo)?.pila ?? null
              }
              candidatos={candidatos}
              configurada={pilas[etapa.modelo]}
              guardando={guardando}
              onGuardar={(pila) => guardar(etapa.modelo, pila)}
            />
          ))}

          <p className="mt-5 text-xs text-[var(--color-ink-muted)]">{t('pila.loQueNoAparece')}</p>
        </>
      )}

      {fallo && (
        <p className="mt-3 rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/5 p-3 text-xs break-words">
          {fallo}
        </p>
      )}
    </Card>
  );
}

export function Modelos() {
  const { t } = useT();
  const estado = useApi<Catalogo>('/api/settings');

  if (estado.error) return <ErrorBanner error={estado.error} onRetry={estado.reload} />;
  if (!estado.data) return <Loading />;

  const { settings } = estado.data;
  const deImagen = estado.data.catalogoDeModelos ?? [];
  const nsfw = estado.data.catalogoNsfw;
  const editores = estado.data.editoresDeImagen ?? [];

  /** Los que sirven para una etapa, con el proveedor a la vista y los no disponibles marcados. */
  const paraEtapa = (etapa: 1 | 2 | 3) =>
    deImagen
      .filter((entrada) => entrada.etapas.includes(etapa))
      .map((entrada) => ({
        value: entrada.model,
        label: `${entrada.etiqueta} · ${entrada.provider}${entrada.disponible ? '' : t('mod.faltaCredencial')}`,
        disabled: !entrada.disponible,
      }));

  const deVideo = (nsfw?.video ?? []).map((m) => ({ value: m.id, label: m.etiqueta }));

  const planes = estado.data.planPorPerfil ?? {};
  const perfiles = Object.keys(planes).map((clave) => {
    const nombre = NOMBRE_DEL_PERFIL[clave];
    return { value: clave, label: nombre ? t(nombre) : clave };
  });

  /** Qué modelo pone hoy el perfil elegido. Es la respuesta a «¿y si no toco nada?». */
  const perfilActivo = planes[settings.imageQualityProfile ?? 'balanced']?.[3];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold">{t('mod.titulo')}</h1>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{t('mod.bajada')}</p>
      </div>

      {/*
        Publicable en todas, primero.

        Es lo que produce la mayor parte del contenido y lo que sale a Instagram y Facebook. El
        material para adultos va después porque es la excepción, no el caso común — y porque su
        primera pregunta es si conviene generarlo, no con qué.
      */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">{t('mod.publicableEnTodas')}</h2>
          <p className="mt-0.5 text-sm text-[var(--color-ink-muted)]">
            {t('mod.publicableEnTodasQue')}
          </p>
        </div>

        <Card title={t('mod.texto')}>
          <Elegir
            clave="textModel"
            titulo={t('mod.modeloDeLenguaje')}
            detalle={t('mod.modeloDeLenguajeQue')}
            valor={settings.textModel ?? null}
            vacio={
              estado.data.catalogoDeTexto?.[0]
                ? t('mod.recomendado', { cual: estado.data.catalogoDeTexto[0].etiqueta })
                : 'Recomendado'
            }
            opciones={(estado.data.catalogoDeTexto ?? []).map((m) => ({
              value: m.air,
              label: m.etiqueta,
            }))}
          />
        </Card>

        {/*
          Describir imágenes es otra tarea que escribir, y por eso es otro selector.

          Este modelo mira una foto y devuelve palabras. Lo usa el set de entrenamiento del LoRA:
          cada imagen va con un caption que nombra lo VARIABLE —encuadre, pose, ropa, luz— y calla
          lo que define la identidad. Lo que el caption explica es lo que el LoRA deja de aprender,
          así que un modelo que se entusiasme describiendo la cara arruina el entrenamiento.
        */}
        <Card title={t('mod.descripcionDeImagenes')}>
          <Elegir
            clave="captionModel"
            titulo={t('mod.modeloQueDescribe')}
            detalle={t('mod.modeloQueDescribeQue')}
            valor={settings.captionModel ?? null}
            vacio={
              estado.data.catalogoDeCaption?.[0]
                ? t('mod.recomendado', { cual: estado.data.catalogoDeCaption[0].etiqueta })
                : 'Recomendado'
            }
            opciones={(estado.data.catalogoDeCaption ?? []).map((m) => ({
              value: m.air,
              label: m.etiqueta,
            }))}
          />
        </Card>

        <Card title="Imagen">
          <div className="space-y-4">
            {/*
              El perfil va primero porque es de quién dependen los cuatro de abajo.

              Cada etapa ofrece «el que diga el perfil de calidad» como valor por omisión. Sin este
              desplegable esa frase señalaba a un ajuste que no se podía ver ni cambiar desde
              ningún lado: un valor por omisión invisible es el peor de los dos mundos, porque
              decide y no se deja auditar.
            */}
            <Elegir
              clave="imageQualityProfile"
              titulo={t('mod.perfilDeCalidad')}
              detalle={
                perfilActivo
                  ? t('mod.perfilDeCalidadHoy', { cual: perfilActivo })
                  : t('mod.perfilDeCalidadQue')
              }
              valor={settings.imageQualityProfile ?? null}
              opciones={perfiles}
            />
            <Elegir
              clave="frontalCandidateModel"
              titulo={t('mod.canonica')}
              detalle={t('mod.canonicaQue')}
              valor={settings.frontalCandidateModel ?? null}
              vacio={t('mod.elQueDigaElPerfil')}
              opciones={paraEtapa(1)}
            />
            <Elegir
              clave="identitySheetModel"
              titulo={t('mod.hojaDeIdentidad')}
              detalle={t('mod.hojaDeIdentidadQue')}
              valor={settings.identitySheetModel ?? null}
              vacio={t('mod.elQueDigaElPerfil')}
              opciones={paraEtapa(2)}
            />
            <Elegir
              clave="contentSetModel"
              titulo={t('mod.contenidoHastaPlaya')}
              detalle={t('mod.contenidoHastaPlayaQue')}
              valor={settings.contentSetModel ?? null}
              vacio={t('mod.elQueDigaElPerfil')}
              opciones={paraEtapa(3)}
            />
            <Elegir
              clave="contentSetModelAtrevido"
              titulo={t('mod.contenidoSensual')}
              detalle={t('mod.contenidoSensualQue')}
              valor={settings.contentSetModelAtrevido ?? null}
              vacio={t('mod.elMismoQueContenido')}
              opciones={paraEtapa(3)}
            />
          </div>
        </Card>

        <Card title={t('mod.video')}>
          <p className="mb-3 text-xs text-[var(--color-ink-muted)]">{t('mod.videoQue')}</p>
          <div className="space-y-4">
            <Elegir
              clave="videoModel"
              titulo={t('mod.hastaPlaya')}
              detalle={t('mod.hastaPlayaQue')}
              valor={settings.videoModel ?? null}
              vacio={t('mod.recomendadoSolo')}
              opciones={deVideo}
            />
            <Elegir
              clave="videoModelAtrevido"
              titulo="Sensual y atrevido"
              detalle={t('mod.sensualYAtrevidoQue')}
              valor={settings.videoModelAtrevido ?? null}
              vacio={t('mod.elMismoQueArriba')}
              opciones={deVideo}
            />
          </div>
        </Card>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">{t('mod.soloFanvue')}</h2>
          <p className="mt-0.5 text-sm text-[var(--color-ink-muted)]">{t('mod.soloFanvueQue')}</p>
        </div>

        {/*
          El modelo de lenguaje del material explícito, aparte del de lo apto.

          ── Por qué son dos selectores y no uno ─────────────────────────────────
          Porque los dos textos quieren cosas opuestas. Lo apto quiere el MÁS CAPAZ: el
          planificador arrastra veinticinco reglas numeradas y lo que falla es que se saltea
          alguna. Lo explícito quiere el que NO MODERA, aunque sea menos capaz, porque un
          proveedor con filtro no escribe peor: contesta que no.

          Y ese fallo no se ve al configurarlo. Poner un modelo de Cloudflare para todo mejora
          las fichas y apaga la propuesta de escenas de Fanvue, semanas después, con un síntoma
          —«ya no propone nada»— que no apunta al selector que se tocó.

          Es el mismo motivo por el que la imagen y el video ya tenían el suyo.
        */}
        <Card title={t('mod.texto')}>
          <Elegir
            clave="textModelNsfw"
            titulo={t('mod.modeloDeLenguaje')}
            detalle={t('mod.modeloDeLenguajeAdultos')}
            valor={settings.textModelNsfw ?? null}
            vacio={t('mod.elMismoQueLoApto')}
            opciones={(estado.data.catalogoDeTexto ?? []).map((m) => ({
              value: m.air,
              label: m.etiqueta,
            }))}
          />
        </Card>

        <Card title="Imagen y video">
          <div className="space-y-4">
            <Elegir
              clave="nsfwImageModel"
              titulo="Imagen"
              detalle={t('mod.identidadPorMecanismo')}
              valor={settings.nsfwImageModel ?? null}
              vacio={nsfw?.imagen[0] ? `Recomendado — ${nsfw.imagen[0].etiqueta}` : 'Recomendado'}
              opciones={(nsfw?.imagen ?? []).map((m) => ({ value: m.id, label: m.etiqueta }))}
            />
            <Elegir
              clave="nsfwVideoModel"
              titulo="Video"
              detalle={t('mod.porGeneracion')}
              valor={settings.nsfwVideoModel ?? null}
              vacio={nsfw?.video[0] ? `Recomendado — ${nsfw.video[0].etiqueta}` : 'Recomendado'}
              opciones={deVideo}
            />

            {/*
              El campo libre, junto a la lista y no escondido.

              Runware aloja miles de modelos de la comunidad y su documentación no publica los
              identificadores de todos. Inventarlos en el catálogo sería más cómodo y menos
              honesto: un AIR equivocado no falla al guardarse, falla al generar.
            */}
            <div>
              <label htmlFor="air-propio" className="block text-sm font-medium">
                {t('mod.oPegaOtro')}
              </label>
              <input
                id="air-propio"
                type="text"
                defaultValue={settings.nsfwImageModel?.includes(':') ? settings.nsfwImageModel : ''}
                onBlur={(event) =>
                  void api.patch('/api/settings', {
                    nsfwImageModel: event.target.value.trim() || null,
                  })
                }
                placeholder="civitai:257749@290640"
                className="mt-1.5 w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 font-mono text-sm"
              />
              <p className="mt-1.5 text-xs text-[var(--color-ink-muted)]">{nsfw?.faltan}</p>
            </div>

            {/*
              El LoRA de contenido, apilado sobre el de identidad.

              Las dos cosas que Fanvue necesita viven en modelos distintos: FLUX permite entrenar
              la identidad y no genera desnudez —su base se entrenó con datos filtrados— y SDXL
              genera desnudez pero Runware no entrena LoRAs para él.

              Apilar es la salida que no obliga a elegir: el campo de Runware es un arreglo. Uno
              pone la cara, el otro pone lo que el modelo base no sabe hacer.
            */}
            <div>
              <label htmlFor="lora-nsfw" className="block text-sm font-medium">
                {t('mod.loraAdulto')}
              </label>
              <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
                {t('mod.loraAdultoQue')}
              </p>
              <input
                id="lora-nsfw"
                type="text"
                defaultValue={settings.nsfwLoraAir ?? ''}
                onBlur={(event) =>
                  void api.patch('/api/settings', {
                    nsfwLoraAir: event.target.value.trim() || null,
                  })
                }
                placeholder="civitai:652699@730201"
                className="mt-1.5 w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 font-mono text-sm"
              />

              {/*
                El disparador, que sin él el LoRA viaja y no hace nada.

                Muchos se entrenan con una palabra clave: `FLUX NSFW unlock` pide
                `aidmaNSWFunlock`. Es el mismo mecanismo que el LoRA de identidad y el mismo modo
                de fallo — la imagen sale, se cobra, y el LoRA no participó. Vacío es legítimo:
                hay LoRA que no llevan ninguno.
              */}
              <input
                type="text"
                defaultValue={settings.nsfwLoraDisparador ?? ''}
                onBlur={(event) =>
                  void api.patch('/api/settings', {
                    nsfwLoraDisparador: event.target.value.trim() || null,
                  })
                }
                placeholder={t('mod.disparadorEjemplo')}
                className="mt-2 w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 font-mono text-sm"
              />

              {/*
                El peso NO está acá, y es a propósito.

                Se ajusta junto con el del LoRA de identidad —compiten entre sí— y ese vive en la
                ficha de cada influencer. Separados, cada prueba obligaba a cambiar uno acá, ir a
                la otra pestaña a cambiar el otro, y volver.

                Acá queda la configuración: cuál es, con qué palabra se activa y sobre qué
                arquitectura. Lo que se toca en cada vuelta está en la pestaña LoRA.
              */}
              <div className="mt-2 flex flex-wrap gap-3">
                <Elegir
                  clave="nsfwLoraFamilia"
                  titulo={t('mod.arquitectura')}
                  detalle={t('mod.arquitecturaQue')}
                  valor={settings.nsfwLoraFamilia ?? null}
                  vacio="FLUX"
                  opciones={[
                    { value: 'flux', label: 'FLUX' },
                    { value: 'sdxl', label: 'SDXL' },
                    { value: 'z-image', label: 'Z-Image' },
                  ]}
                />
              </div>
            </div>

            <p className="text-xs text-[var(--color-warn)]">{nsfw?.nota}</p>
          </div>
        </Card>

        {/*
          El segundo paso, y por qué merece su propia tarjeta.

          Todo lo de arriba configura UN modelo que tiene que hacer las dos cosas: la cara de ella
          y el cuerpo desnudo. Ninguno de los probados hace las dos —FLUX aprende la cara y no
          desnuda, SDXL desnuda y no tiene LoRA— así que esto es el otro camino: generar con el
          que sabe hacer el cuerpo y ponerle la cara después.

          Se elige por toma, con el botón «Con cambio de cara» de la pantalla de Fanvue. Acá sólo
          se dice con qué modelo.
        */}
        <Card title={t('mod.cambioDeCara')}>
          <p className="text-sm text-[var(--color-ink-muted)]">{t('mod.cambioDeCaraQue')}</p>

          <div className="mt-3">
            <Elegir
              clave="faceSwapModel"
              titulo={t('mod.conQueSeCambia')}
              detalle={t('mod.conQueSeCambiaQue')}
              valor={settings.faceSwapModel ?? null}
              vacio={
                editores[0]
                  ? t('mod.recomendado', { cual: editores[0].etiqueta })
                  : t('mod.sinEditores')
              }
              opciones={editores.map((editor) => ({
                value: editor.air,
                label:
                  t('mod.porImagen', {
                    cual: editor.etiqueta,
                    usd: editor.usdPorSalida.toFixed(3),
                  }) + (editor.sinFiltro ? '' : t('mod.conFiltro')),
              }))}
            />
          </div>

          {/*
            La nota del elegido, entera y visible.

            Es donde dice lo que no se puede saber del nombre ni del precio: si trae filtro, y —en
            el caso del recomendado— que no traerlo no garantiza que el cambio de cara salga bien.
            Eso se sabe generando una, y conviene que se lea antes de gastar.
          */}
          {(() => {
            const elegido =
              editores.find((editor) => editor.air === settings.faceSwapModel) ?? editores[0];
            if (!elegido) return null;
            return (
              <p
                className={`mt-3 rounded-xl border p-3 text-xs ${
                  elegido.sinFiltro
                    ? 'border-[var(--color-border-subtle)]'
                    : 'border-[var(--color-warn)]/35 bg-[var(--color-warn)]/10'
                }`}
              >
                {elegido.nota}
              </p>
            );
          })()}
        </Card>
      </section>

      {/*
        Sólo con la placa elegida en alguna etapa. La pila se aplica dentro del grafo de ComfyUI;
        con Runware o Workers AI ese grafo no existe y estas perillas no moverían nada.
      */}
      {[
        settings.frontalCandidateModel,
        settings.identitySheetModel,
        settings.contentSetModel,
        settings.contentSetModelAtrevido,
        settings.nsfwImageModel,
      ].some((m) => typeof m === 'string' && m.startsWith('local-gpu/')) && (
        <section>
          <PilaDeLaPlaca settings={settings} onGuardado={estado.reload} />
          <FormaDelCuerpo settings={settings} onGuardado={estado.reload} />
        </section>
      )}
    </div>
  );
}
